import { useEffect, useRef, useState, useCallback } from "react";
import { useParams } from "wouter";
import { useTranslation } from "react-i18next";
import { io, Socket } from "socket.io-client";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { getApiUrl, API_URL, ApiError } from "@/lib/api";
import i18n, { applyTenantLanguage, chooseLanguage } from "@/lib/i18n";
import {
  isTerminalTrackingStatus,
  type TrackingSessionStatus,
} from "@shared/tracking";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PublicSession {
  id: string;
  busNumber: string;
  eventName: string;
  status: TrackingSessionStatus;
  driverLat?: number | null;
  driverLng?: number | null;
  driverLastSeen?: string | null;
  coordinatorLat?: number | null;
  coordinatorLng?: number | null;
  meetupLat?: number | null;
  meetupLng?: number | null;
  meetupLabel?: string | null;
  lastStatusMessage?: string | null;
  boardedCount?: number;
  totalPassengers?: number;
  /** tenants.default_language: the page's language unless the passenger picks one. */
  defaultLanguage?: string | null;
  tripInfo?: {
    departureTime: string;
    routeName: string | null;
    stops: Array<{ name: string; lat: number | null; lng: number | null; order: number }>;
  } | null;
}

interface CachedState {
  driverLat?: number | null;
  driverLng?: number | null;
  coordinatorLat?: number | null;
  coordinatorLng?: number | null;
  meetupLat?: number | null;
  meetupLng?: number | null;
  lastStatusMessage?: string | null;
  boardedCount?: number;
  totalPassengers?: number;
  updatedAt?: number;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// AT&T Stadium, Arlington TX
const DEFAULT_CENTER: [number, number] = [-97.0929, 32.748];
const DEFAULT_ZOOM = 14;
const MAP_STYLE = "https://tiles.openfreemap.org/styles/liberty";
const LERP_DURATION_MS = 1500;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function readCache(sessionId: string): CachedState | null {
  try {
    const raw = localStorage.getItem(`fleettrack_${sessionId}`);
    if (!raw) return null;
    return JSON.parse(raw) as CachedState;
  } catch {
    return null;
  }
}

function writeCache(sessionId: string, data: CachedState): void {
  try {
    localStorage.setItem(`fleettrack_${sessionId}`, JSON.stringify(data));
  } catch {
    // Storage quota exceeded — best effort
  }
}

function minutesAgo(ts: number | undefined): string {
  if (!ts) return "?";
  const mins = Math.round((Date.now() - ts) / 60000);
  if (mins < 1) return "< 1 min";
  if (mins === 1) return "1 min";
  return `${mins} min`;
}

function lerpMarker(
  marker: maplibregl.Marker,
  toLat: number,
  toLng: number,
  durationMs: number,
): number {
  const from = marker.getLngLat();
  const startTime = performance.now();
  let rafId: number;
  function step(now: number) {
    const t = Math.min(1, (now - startTime) / durationMs);
    try {
      marker.setLngLat([
        from.lng + (toLng - from.lng) * t,
        from.lat + (toLat - from.lat) * t,
      ]);
    } catch {
      return; // marker or map was destroyed — stop the loop
    }
    if (t < 1) rafId = requestAnimationFrame(step);
  }
  rafId = requestAnimationFrame(step);
  return rafId;
}

// ---------------------------------------------------------------------------
// Custom marker elements
// ---------------------------------------------------------------------------

function createBusMarkerElement(connected: boolean): HTMLDivElement {
  const el = document.createElement("div");
  el.style.cssText = `
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: ${connected ? "#3b82f6" : "#6b7280"};
    border: 3px solid #fff;
    box-shadow: 0 2px 8px rgba(0,0,0,0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    cursor: default;
    user-select: none;
  `;
  el.textContent = connected ? "🚌" : "?";
  el.title = connected ? "Bus" : "Driver not connected";
  return el;
}

function createCoordMarkerElement(): HTMLDivElement {
  const el = document.createElement("div");
  el.style.cssText = `
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: #eab308;
    border: 3px solid #fff;
    box-shadow: 0 2px 8px rgba(0,0,0,0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    cursor: default;
    user-select: none;
  `;
  el.textContent = "🚩";
  el.title = "Coordinator";
  return el;
}

function createMeetupMarkerElement(): HTMLDivElement {
  // Outer pulsing ring + inner pin
  const wrapper = document.createElement("div");
  wrapper.style.cssText = `
    position: relative;
    width: 44px;
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
  `;

  const ring = document.createElement("div");
  ring.style.cssText = `
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: rgba(249, 115, 22, 0.35);
    animation: meetup-pulse 1.5s ease-in-out infinite;
  `;

  // Inject keyframes once
  if (!document.getElementById("meetup-pulse-style")) {
    const style = document.createElement("style");
    style.id = "meetup-pulse-style";
    style.textContent = `
      @keyframes meetup-pulse {
        0%, 100% { transform: scale(1); opacity: 0.7; }
        50% { transform: scale(1.3); opacity: 0.3; }
      }
    `;
    document.head.appendChild(style);
  }

  const inner = document.createElement("div");
  inner.style.cssText = `
    position: relative;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: #f97316;
    border: 3px solid #fff;
    box-shadow: 0 2px 8px rgba(0,0,0,0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    cursor: default;
    user-select: none;
    z-index: 1;
  `;
  inner.textContent = "📍";

  wrapper.appendChild(ring);
  wrapper.appendChild(inner);
  return wrapper;
}

function createStopMarkerElement(name: string, isTerminal: boolean): HTMLDivElement {
  const el = document.createElement("div");
  el.style.cssText = `
    display: flex;
    flex-direction: column;
    align-items: center;
    cursor: default;
    user-select: none;
  `;
  const dot = document.createElement("div");
  dot.style.cssText = `
    width: ${isTerminal ? "14px" : "10px"};
    height: ${isTerminal ? "14px" : "10px"};
    border-radius: 50%;
    background: ${isTerminal ? "#ef4444" : "#9ca3af"};
    border: 2px solid white;
    box-shadow: 0 1px 4px rgba(0,0,0,0.3);
  `;
  const label = document.createElement("div");
  label.style.cssText = `
    margin-top: 2px;
    background: rgba(0,0,0,0.7);
    color: white;
    font-size: 10px;
    padding: 1px 4px;
    border-radius: 3px;
    white-space: nowrap;
    font-weight: 500;
  `;
  label.textContent = name;
  el.appendChild(dot);
  el.appendChild(label);
  return el;
}

// Honest live indicator for the info panel: green pulse when the bus reported a
// position recently, amber when the signal has gone quiet, and a clear "waiting
// for the bus" before the first ping. Replaces the old perpetual "Waiting for
// updates…" placeholder (which was the ops broadcast fallback, never sent).
function LiveSignalBadge({
  busLastSeen,
  nowTs,
}: {
  busLastSeen: number | null;
  nowTs: number;
}) {
  const { t } = useTranslation("common");
  const secs = busLastSeen != null ? (nowTs - busLastSeen) / 1000 : null;
  const live = secs != null && secs < 90;
  return (
    <div className="flex items-center gap-2">
      <span
        className={`w-2 h-2 rounded-full shrink-0 ${
          live ? "bg-green-400 animate-pulse" : "bg-amber-400"
        }`}
      />
      <span className={`text-sm ${live ? "text-green-300" : "text-amber-400"}`}>
        {busLastSeen == null
          ? t("track.awaitingBus")
          : live
            ? t("track.liveTracking")
            : t("track.noLiveSignal")}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function TrackPassengerPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const { t } = useTranslation("common");

  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------

  const [session, setSession] = useState<PublicSession | null>(null);

  // First stop → last stop. The route's own name is internal ("Texas to
  // Mexico 2026") and is never shown to passengers.
  const trackStops = [...(session?.tripInfo?.stops ?? [])].sort(
    (a, b) => a.order - b.order,
  );
  const trackRouteLabel =
    trackStops.length >= 2
      ? `${trackStops[0].name} → ${trackStops[trackStops.length - 1].name}`
      : null;
  // An i18n key, translated at render so a later language switch applies.
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [isError, setIsError] = useState(false);
  const [lastStatusMessage, setLastStatusMessage] = useState<string | null>(
    null,
  );
  const [boardedCount, setBoardedCount] = useState<number>(0);
  const [totalPassengers, setTotalPassengers] = useState<number>(0);
  const [isOffline, setIsOffline] = useState(false);
  const [offlineUpdatedAt, setOfflineUpdatedAt] = useState<number | undefined>(
    undefined,
  );
  // When the BUS last reported a position (ms). Drives the live/last-signal
  // badge. Sourced from driverLastSeen on fetch and from socket driver-location
  // events. `nowTs` ticks so the badge flips live→stale as time passes.
  const [busLastSeen, setBusLastSeen] = useState<number | null>(null);
  const [nowTs, setNowTs] = useState(() => Date.now());

  // ---------------------------------------------------------------------------
  // Refs
  // ---------------------------------------------------------------------------

  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const socketRef = useRef<Socket | null>(null);
  const busMarkerRef = useRef<maplibregl.Marker | null>(null);
  const coordMarkerRef = useRef<maplibregl.Marker | null>(null);
  const meetupMarkerRef = useRef<maplibregl.Marker | null>(null);
  const busRafRef = useRef<number | null>(null);
  const coordRafRef = useRef<number | null>(null);
  const meetupRafRef = useRef<number | null>(null);
  const cacheRef = useRef<CachedState>({});
  const stopMarkersRef = useRef<maplibregl.Marker[]>([]);
  const sessionIdRef = useRef(sessionId);
  sessionIdRef.current = sessionId;

  // ---------------------------------------------------------------------------
  // Map initialization — fires immediately, no waiting for session
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!mapContainerRef.current) return;
    if (mapRef.current) return;

    const map = new maplibregl.Map({
      container: mapContainerRef.current,
      style: MAP_STYLE,
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
    });

    map.addControl(new maplibregl.NavigationControl(), "top-right");
    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // ---------------------------------------------------------------------------
  // Marker management helpers
  // ---------------------------------------------------------------------------

  const ensureBusMarker = useCallback(
    (lat: number, lng: number, connected: boolean) => {
      const map = mapRef.current;
      if (!map) return;

      if (busMarkerRef.current) {
        if (busRafRef.current !== null) cancelAnimationFrame(busRafRef.current);
        busRafRef.current = lerpMarker(
          busMarkerRef.current,
          lat,
          lng,
          LERP_DURATION_MS,
        );
      } else {
        const el = createBusMarkerElement(connected);
        busMarkerRef.current = new maplibregl.Marker({ element: el })
          .setLngLat([lng, lat])
          .addTo(map);
      }
    },
    [],
  );

  const ensureCoordMarker = useCallback((lat: number, lng: number) => {
    const map = mapRef.current;
    if (!map) return;

    if (coordMarkerRef.current) {
      if (coordRafRef.current !== null)
        cancelAnimationFrame(coordRafRef.current);
      coordRafRef.current = lerpMarker(
        coordMarkerRef.current,
        lat,
        lng,
        LERP_DURATION_MS,
      );
    } else {
      const el = createCoordMarkerElement();
      coordMarkerRef.current = new maplibregl.Marker({ element: el })
        .setLngLat([lng, lat])
        .addTo(map);
    }
  }, []);

  const ensureMeetupMarker = useCallback((lat: number, lng: number) => {
    const map = mapRef.current;
    if (!map) return;

    if (meetupMarkerRef.current) {
      if (meetupRafRef.current !== null)
        cancelAnimationFrame(meetupRafRef.current);
      meetupRafRef.current = lerpMarker(
        meetupMarkerRef.current,
        lat,
        lng,
        LERP_DURATION_MS,
      );
    } else {
      const el = createMeetupMarkerElement();
      meetupMarkerRef.current = new maplibregl.Marker({ element: el })
        .setLngLat([lng, lat])
        .addTo(map);
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Live-refresh fallback. The Socket.IO stream can stall silently (mobile
  // screen-lock, network switch, server redeploy) with no recovery until a full
  // page reload. Re-fetching /public keeps the map current without a reload.
  // Markers are applied ONLY while the socket is disconnected. A live socket is
  // the fresher source — browser-driver sessions emit over the socket BEFORE the
  // throttled DB write, so moving the marker from /public could drag the bus
  // backward. Status/boarding/last-seen always refresh (cheap, last-write-wins,
  // no marker jump). DB lat/lng come over the wire as decimal STRINGS, so coords
  // are normalized to numbers before compare/store (strict !== would otherwise
  // always look "changed").
  // ---------------------------------------------------------------------------
  const refreshFromServer = useCallback(async () => {
    if (!sessionId || !UUID_RE.test(sessionId)) return;
    try {
      const res = await fetch(
        getApiUrl(`/api/tracking/sessions/${sessionId}/public`),
      );
      if (!res.ok) return; // transient — don't disrupt the live view
      const data = (await res.json()) as PublicSession;

      if (isTerminalTrackingStatus(data.status)) {
        setErrorMsg("track.eventEnded");
        setIsError(true);
        return;
      }

      // A successful poll means we ARE receiving data — clear the socket-down
      // banner so it can't contradict a fresh "Live" badge.
      setIsOffline(false);

      if (data.lastStatusMessage) setLastStatusMessage(data.lastStatusMessage);
      if (data.boardedCount != null) setBoardedCount(data.boardedCount);
      if (data.totalPassengers != null) setTotalPassengers(data.totalPassengers);
      if (data.driverLastSeen) {
        const seen = new Date(data.driverLastSeen).getTime();
        if (Number.isFinite(seen)) setBusLastSeen((p) => Math.max(p ?? 0, seen));
      }

      // Never override a live socket — it streams the freshest position itself.
      if (socketRef.current?.connected) return;

      const lat = data.driverLat != null ? Number(data.driverLat) : null;
      const lng = data.driverLng != null ? Number(data.driverLng) : null;
      const prev = cacheRef.current;
      if (
        lat != null &&
        lng != null &&
        Number.isFinite(lat) &&
        Number.isFinite(lng) &&
        (lat !== prev.driverLat || lng !== prev.driverLng)
      ) {
        ensureBusMarker(lat, lng, false);
        const next: CachedState = {
          ...prev,
          driverLat: lat,
          driverLng: lng,
          updatedAt: Date.now(),
        };
        cacheRef.current = next;
        writeCache(sessionId, next);
        setOfflineUpdatedAt(next.updatedAt);
      }

      if (data.coordinatorLat != null && data.coordinatorLng != null) {
        ensureCoordMarker(
          Number(data.coordinatorLat),
          Number(data.coordinatorLng),
        );
      }
      if (data.meetupLat != null && data.meetupLng != null) {
        ensureMeetupMarker(Number(data.meetupLat), Number(data.meetupLng));
      }
    } catch {
      // transient poll/network error — ignore; the next tick retries
    }
  }, [sessionId, t, ensureBusMarker, ensureCoordMarker, ensureMeetupMarker]);

  // ---------------------------------------------------------------------------
  // Session fetch + initial marker placement
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!sessionId || !UUID_RE.test(sessionId)) {
      setErrorMsg("track.invalidLink");
      setIsError(true);
      return;
    }

    // Restore from localStorage immediately
    const cached = readCache(sessionId);
    if (cached) {
      cacheRef.current = cached;
      if (cached.lastStatusMessage)
        setLastStatusMessage(cached.lastStatusMessage);
      if (cached.boardedCount != null) setBoardedCount(cached.boardedCount);
      if (cached.totalPassengers != null)
        setTotalPassengers(cached.totalPassengers);
      setOfflineUpdatedAt(cached.updatedAt);

      // Place markers from cache while we wait for socket
      if (cached.driverLat != null && cached.driverLng != null) {
        // Wait briefly for map to be ready
        setTimeout(() => {
          ensureBusMarker(cached.driverLat!, cached.driverLng!, false);
        }, 500);
      }
      if (cached.coordinatorLat != null && cached.coordinatorLng != null) {
        setTimeout(() => {
          ensureCoordMarker(cached.coordinatorLat!, cached.coordinatorLng!);
        }, 500);
      }
      if (cached.meetupLat != null && cached.meetupLng != null) {
        setTimeout(() => {
          ensureMeetupMarker(cached.meetupLat!, cached.meetupLng!);
        }, 500);
      }
    }

    let cancelled = false;

    fetch(getApiUrl(`/api/tracking/sessions/${sessionId}/public`))
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new ApiError(
            res.status,
            (body as { message?: string }).message || "Not found",
          );
        }
        return res.json() as Promise<PublicSession>;
      })
      .then((data) => {
        if (cancelled) return;
        // The tenant's language, before any branch, so an ended session's
        // message speaks it too (see applyTenantLanguage).
        applyTenantLanguage(data.defaultLanguage);
        // Cancelled sessions are treated exactly like ended — tracking is over
        if (isTerminalTrackingStatus(data.status)) {
          setErrorMsg("track.eventEnded");
          setIsError(true);
          return;
        }
        setSession(data);

        if (data.lastStatusMessage)
          setLastStatusMessage(data.lastStatusMessage);
        if (data.boardedCount != null) setBoardedCount(data.boardedCount);
        if (data.totalPassengers != null)
          setTotalPassengers(data.totalPassengers);
        if (data.driverLastSeen)
          setBusLastSeen(new Date(data.driverLastSeen).getTime());

        // Fly to driver position on map if available
        const map = mapRef.current;
        if (map && data.driverLat != null && data.driverLng != null) {
          map.flyTo({ center: [data.driverLng, data.driverLat], zoom: 15 });
          ensureBusMarker(data.driverLat, data.driverLng, true);
        } else if (map && data.driverLat == null) {
          // Place placeholder at default center
          ensureBusMarker(DEFAULT_CENTER[1], DEFAULT_CENTER[0], false);
        }
        if (data.coordinatorLat != null && data.coordinatorLng != null) {
          ensureCoordMarker(data.coordinatorLat, data.coordinatorLng);
        }
        if (data.meetupLat != null && data.meetupLng != null) {
          ensureMeetupMarker(data.meetupLat, data.meetupLng);
        }

        // Add route stop markers for trip-linked sessions
        if (data.tripInfo?.stops && map) {
          // Remove previous stop markers
          for (const m of stopMarkersRef.current) m.remove();
          stopMarkersRef.current = [];

          const stopsWithCoords = data.tripInfo.stops.filter(
            (s) => s.lat != null && s.lng != null,
          );
          for (let i = 0; i < stopsWithCoords.length; i++) {
            const stop = stopsWithCoords[i];
            const isTerminal = i === 0 || i === stopsWithCoords.length - 1;
            const el = createStopMarkerElement(stop.name, isTerminal);
            const marker = new maplibregl.Marker({ element: el })
              .setLngLat([stop.lng!, stop.lat!])
              .addTo(map);
            stopMarkersRef.current.push(marker);
          }
        }
      })
      .catch(() => {
        if (!cancelled) {
          setErrorMsg("track.connectionError");
          setIsError(true);
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // ---------------------------------------------------------------------------
  // Polling fallback + focus refetch. Keeps the map current when the socket
  // stalls, and refreshes instantly when the tab regains focus (mobile
  // screen-lock/return). Only runs while the tab is visible — no need to poll a
  // backgrounded tab. `nowTs` ticks here so the live badge ages correctly.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!sessionId || !UUID_RE.test(sessionId)) return;

    const interval = setInterval(() => {
      if (document.visibilityState !== "visible") return;
      setNowTs(Date.now()); // local tick so the live badge ages
      // Poll only when the realtime socket is down — a connected socket already
      // streams location/status/boarding, so healthy tabs make ~zero requests
      // (keeps us off the public rate limit behind shared NAT at scale).
      if (!socketRef.current?.connected) refreshFromServer();
    }, 10_000);

    // Refetch immediately on tab-focus (mobile screen-lock/return). One-shot and
    // cheap; marker writes inside are still gated on socket state.
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      setNowTs(Date.now());
      refreshFromServer();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [sessionId, refreshFromServer]);

  // ---------------------------------------------------------------------------
  // Socket.IO — connect immediately on page load
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!sessionId || !UUID_RE.test(sessionId)) return;

    const base = API_URL || window.location.origin;
    const socket = io(`${base}/tracking`, {
      path: "/socket.io",
      transports: ["websocket", "polling"],
    });
    socketRef.current = socket;

    let heartbeatInterval: ReturnType<typeof setInterval> | null = null;

    socket.on("connect", () => {
      setIsOffline(false);
      socket.emit("join-tracking-session", {
        sessionId,
        role: "passenger",
      });
    });

    socket.on("joined", () => {
      // Emit initial heartbeat immediately, then every 30s
      socket.emit("passenger-heartbeat");
      heartbeatInterval = setInterval(() => {
        socket.emit("passenger-heartbeat");
      }, 30_000);
    });

    socket.on("disconnect", () => {
      setIsOffline(true);
      const cached = cacheRef.current;
      setOfflineUpdatedAt(cached.updatedAt);
    });

    socket.on(
      "driver-location",
      ({ lat, lng }: { lat: number; lng: number }) => {
        ensureBusMarker(lat, lng, true);
        setBusLastSeen(Date.now());
        const next: CachedState = {
          ...cacheRef.current,
          driverLat: lat,
          driverLng: lng,
          updatedAt: Date.now(),
        };
        cacheRef.current = next;
        writeCache(sessionId, next);
        setOfflineUpdatedAt(next.updatedAt);
      },
    );

    socket.on(
      "coordinator-location",
      ({ lat, lng }: { lat: number; lng: number }) => {
        ensureCoordMarker(lat, lng);
        const next: CachedState = {
          ...cacheRef.current,
          coordinatorLat: lat,
          coordinatorLng: lng,
          updatedAt: Date.now(),
        };
        cacheRef.current = next;
        writeCache(sessionId, next);
        setOfflineUpdatedAt(next.updatedAt);
      },
    );

    socket.on(
      "meetup-update",
      ({ lat, lng }: { lat: number; lng: number; label?: string }) => {
        ensureMeetupMarker(lat, lng);
        const next: CachedState = {
          ...cacheRef.current,
          meetupLat: lat,
          meetupLng: lng,
          updatedAt: Date.now(),
        };
        cacheRef.current = next;
        writeCache(sessionId, next);
        setOfflineUpdatedAt(next.updatedAt);
      },
    );

    socket.on("status-broadcast", ({ message }: { message: string }) => {
      setLastStatusMessage(message);
      const next: CachedState = {
        ...cacheRef.current,
        lastStatusMessage: message,
        updatedAt: Date.now(),
      };
      cacheRef.current = next;
      writeCache(sessionId, next);
      setOfflineUpdatedAt(next.updatedAt);
    });

    socket.on(
      "boarding-update",
      ({
        boardedCount: boarded,
        totalPassengers: total,
      }: {
        boardedCount: number;
        totalPassengers: number;
      }) => {
        setBoardedCount(boarded);
        setTotalPassengers(total);
        const next: CachedState = {
          ...cacheRef.current,
          boardedCount: boarded,
          totalPassengers: total,
          updatedAt: Date.now(),
        };
        cacheRef.current = next;
        writeCache(sessionId, next);
        setOfflineUpdatedAt(next.updatedAt);
      },
    );

    return () => {
      if (heartbeatInterval) clearInterval(heartbeatInterval);
      socket.disconnect();
      socketRef.current = null;
    };
    // ensureBus/Coord/Meetup are stable callbacks — safe to omit from deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // ---------------------------------------------------------------------------
  // Cleanup markers on unmount
  // ---------------------------------------------------------------------------

  useEffect(() => {
    return () => {
      // Cancel in-flight rAF loops before removing markers
      if (busRafRef.current !== null) cancelAnimationFrame(busRafRef.current);
      if (coordRafRef.current !== null)
        cancelAnimationFrame(coordRafRef.current);
      if (meetupRafRef.current !== null)
        cancelAnimationFrame(meetupRafRef.current);
      busMarkerRef.current?.remove();
      coordMarkerRef.current?.remove();
      meetupMarkerRef.current?.remove();
      for (const m of stopMarkersRef.current) m.remove();
      stopMarkersRef.current = [];
    };
  }, []);

  // ---------------------------------------------------------------------------
  // Language toggle
  // ---------------------------------------------------------------------------

  const setLang = (lang: string) => {
    chooseLanguage(lang);
  };

  const currentLang = i18n.language?.startsWith("es") ? "es" : "en";

  // ---------------------------------------------------------------------------
  // Error overlay
  // ---------------------------------------------------------------------------

  if (isError) {
    return (
      <div
        style={{ width: "100vw", height: "100dvh" }}
        className="bg-gray-950 flex items-center justify-center p-6"
      >
        <div className="bg-gray-800 rounded-2xl p-8 max-w-sm w-full text-center">
          <div className="text-5xl mb-4">🚫</div>
          <h1 className="text-xl font-bold text-white mb-2">
            {t("track.invalidLink")}
          </h1>
          <p className="text-gray-400 text-sm">{errorMsg && t(errorMsg)}</p>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Main render — full-viewport map + fixed bottom sheet
  // ---------------------------------------------------------------------------

  return (
    <div style={{ width: "100vw", height: "100dvh", position: "relative" }}>
      {/* Offline banner */}
      {isOffline && (
        <div
          style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 50 }}
          className="bg-amber-500 px-4 py-2 flex items-center justify-center"
          role="alert"
          aria-live="assertive"
        >
          <span className="text-gray-900 text-sm font-medium">
            {t("track.offlineBanner", { time: minutesAgo(offlineUpdatedAt) })}
          </span>
        </div>
      )}

      {/* Map container — fills entire viewport */}
      <div
        ref={mapContainerRef}
        style={{ width: "100vw", height: "100dvh" }}
        aria-label="Fleet tracker map"
      />

      {/* Bottom sheet */}
      <div
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          maxHeight: "40vh",
          zIndex: 40,
        }}
        className="bg-gray-900/95 rounded-t-2xl shadow-2xl px-4 pt-3 pb-4 flex flex-col gap-2"
        role="complementary"
        aria-label="Trip info"
      >
        {/* Top row: bus badge + event name + lang toggle */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {session?.busNumber && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-blue-600 text-white text-xs font-bold whitespace-nowrap shrink-0">
                {t("track.busNumber", { number: session.busNumber })}
              </span>
            )}
            {trackRouteLabel ? (
              <p className="text-white text-sm font-semibold truncate">
                {trackRouteLabel}
              </p>
            ) : session?.eventName ? (
              <p className="text-white text-sm font-semibold truncate">
                {session.eventName}
              </p>
            ) : null}
          </div>

          {/* EN | ES toggle */}
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              onClick={() => setLang("en")}
              aria-pressed={currentLang === "en"}
              className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-colors ${
                currentLang === "en"
                  ? "bg-white text-gray-900"
                  : "bg-gray-700 text-gray-400 hover:bg-gray-600"
              }`}
            >
              EN
            </button>
            <button
              type="button"
              onClick={() => setLang("es")}
              aria-pressed={currentLang === "es"}
              className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-colors ${
                currentLang === "es"
                  ? "bg-white text-gray-900"
                  : "bg-gray-700 text-gray-400 hover:bg-gray-600"
              }`}
            >
              ES
            </button>
          </div>
        </div>

        {/* Ops status broadcast if one exists; otherwise an honest live signal */}
        {lastStatusMessage ? (
          <p className="text-gray-300 text-sm leading-snug line-clamp-2">
            {lastStatusMessage}
          </p>
        ) : (
          <LiveSignalBadge busLastSeen={busLastSeen} nowTs={nowTs} />
        )}

        {/* Boarding count */}
        {boardedCount > 0 && (
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-green-400 shrink-0" />
            <span className="text-green-300 text-sm font-medium">
              {t("track.aboardCount", {
                boarded: boardedCount,
                total: totalPassengers,
              })}
            </span>
          </div>
        )}

        {/* Last known position note when offline */}
        {isOffline && offlineUpdatedAt && (
          <p className="text-amber-400 text-xs">
            {t("track.showingLastKnown")}
          </p>
        )}
      </div>
    </div>
  );
}
